/**
 * Root Entity archetype.
 *
 * Per the Trust Network design (see docs/root-entity-archetype.md and the
 * Strategy & Architecture document, Part II §7), every tenant in the
 * network has one of three equal-status archetypes:
 *
 *   - client            : a Client Company — owns compliance data, evidence
 *                         vault, Trust Center, subsidiaries
 *   - consulting_firm   : a Consulting Firm — owns practitioner roster,
 *                         proprietary playbook IP, engagement portfolio
 *   - audit_firm        : an independent Audit Firm — owns auditor
 *                         credentials, tamper-evident working papers
 *
 * The archetype is structural, not hierarchical. A tenant's *position* in
 * the tree (root / group / leaf) is independent of its archetype.
 *
 * Today the backend column does not yet exist. The frontend treats the
 * field as optional and falls back to `client` for backward compatibility.
 * When the backend ships `tenants.root_entity_type NOT NULL DEFAULT 'client'`,
 * the fallback becomes dead code and can be removed.
 */

export type RootEntityType =
  | 'client'
  | 'consulting_firm'
  | 'audit_firm'
  | 'regulator'
  | 'platform_operator';

export const ROOT_ENTITY_TYPES: RootEntityType[] = [
  'client',
  'consulting_firm',
  'audit_firm',
  'regulator',
  'platform_operator',
];

export const ROOT_ENTITY_LABEL: Record<RootEntityType, string> = {
  client: 'Client Company',
  consulting_firm: 'Consulting Firm',
  audit_firm: 'Audit Firm',
  regulator: 'Regulator',
  platform_operator: 'Platform Operator',
};

export const ROOT_ENTITY_LABEL_AR: Record<RootEntityType, string> = {
  client: 'شركة عميلة',
  consulting_firm: 'شركة استشارية',
  audit_firm: 'شركة تدقيق',
  regulator: 'جهة تنظيمية',
  platform_operator: 'مشغّل المنصة',
};

export const ROOT_ENTITY_DESCRIPTION: Record<RootEntityType, string> = {
  client:
    'Owns compliance data, evidence vault, Trust Center and subsidiaries. Invites consultants and auditors via federation.',
  consulting_firm:
    'Owns practitioner roster, proprietary playbook IP and engagement portfolio. Federates into clients to deliver outcomes.',
  audit_firm:
    'Owns auditor credentials and tamper-evident working papers. Federates into clients for independent assurance.',
  regulator:
    'Receives supervisory submissions from regulated entities. Read-oriented oversight; does not deliver engagements.',
  platform_operator:
    'Pianat HQ. Operates the platform: manages all tenants, cross-tenant audit, and support impersonation.',
};

/** Tint maps used by RootEntityBadge and any other badge-style display. */
export const ROOT_ENTITY_TINT: Record<RootEntityType, string> = {
  client: 'bg-blue-100 text-blue-800 ring-blue-200',
  consulting_firm: 'bg-violet-100 text-violet-800 ring-violet-200',
  audit_firm: 'bg-amber-100 text-amber-800 ring-amber-200',
  regulator: 'bg-rose-100 text-rose-800 ring-rose-200',
  platform_operator: 'bg-emerald-100 text-emerald-800 ring-emerald-200',
};

export const ROOT_ENTITY_DOT: Record<RootEntityType, string> = {
  client: 'bg-blue-500',
  consulting_firm: 'bg-violet-500',
  audit_firm: 'bg-amber-500',
  regulator: 'bg-rose-500',
  platform_operator: 'bg-emerald-500',
};

/** Default archetype used when the backend hasn't sent one yet. */
export const DEFAULT_ROOT_ENTITY_TYPE: RootEntityType = 'client';

/**
 * Derive the effective archetype from a tenant-shaped object. Tolerates
 * missing / null / unknown values. Use this everywhere the UI branches on
 * archetype so the backward-compat fallback lives in one place.
 */
export function resolveRootEntityType(
  value: string | null | undefined,
): RootEntityType {
  if (
    value === 'client' ||
    value === 'consulting_firm' ||
    value === 'audit_firm' ||
    value === 'regulator' ||
    value === 'platform_operator'
  ) {
    return value;
  }
  return DEFAULT_ROOT_ENTITY_TYPE;
}

/**
 * What can a tenant of this archetype *do* on the Trust Network? Used by
 * UI to hide tabs and actions that don't make sense for the archetype.
 */
export interface ArchetypeCapabilities {
  /** Maintains a list of certified practitioners. */
  hasPractitioners: boolean;
  /** Authors and deploys playbooks via federation. */
  hasPlaybookIp: boolean;
  /** Holds the canonical evidence vault for the engagement. */
  ownsEvidenceVault: boolean;
  /** Has a tamper-evident audit workspace. */
  hasAuditWorkspace: boolean;
  /** Can be the originator of a Service Provider Request. */
  canFederateOutbound: boolean;
  /** Can receive a Service Provider Request. */
  canFederateInbound: boolean;
  /**
   * Has the Pianat-as-root partner-network view (directory, scorecard,
   * onboarding pipeline). Also gates the client-portal "Consultants" sidebar.
   * @deprecated Granted to `client` as a legacy placeholder. `platform_operator`
   * now exists; migrate platform-level UI gating to `canManageAllTenants` and
   * narrow/remove the `client` grant once the Consultants surface gets its own
   * capability.
   */
  canManagePartners: boolean;
  /** NEW (Phase 0): manage every tenant on the platform. platform_operator only. */
  canManageAllTenants: boolean;
  /** NEW (Phase 0): impersonate any tenant for support. platform_operator only. */
  canImpersonateAnyTenant: boolean;
  /** NEW (Phase 0): read the cross-tenant audit log. platform_operator only. */
  canAccessCrossTenantAudit: boolean;
  /** NEW (Phase 4): manage user-tenant memberships + the tenant-admin
   *  surface. Mirrors backend src/shared/archetype-capabilities.ts. The
   *  sidebar additionally requires subtree scope (non-leaf tenant). */
  canManageMemberships: boolean;
}

export const ROOT_ENTITY_CAPABILITIES: Record<RootEntityType, ArchetypeCapabilities> = {
  client: {
    hasPractitioners: false,
    hasPlaybookIp: false,
    ownsEvidenceVault: true,
    hasAuditWorkspace: false,
    canFederateOutbound: false,
    canFederateInbound: true,
    canManagePartners: true, // @deprecated legacy placeholder — see interface
    canManageAllTenants: false,
    canImpersonateAnyTenant: false,
    canAccessCrossTenantAudit: false,
    canManageMemberships: true, // banks with branch sub-tenants (Phase 4)
  },
  consulting_firm: {
    hasPractitioners: true,
    hasPlaybookIp: true,
    ownsEvidenceVault: false,
    hasAuditWorkspace: false,
    canFederateOutbound: true,
    canFederateInbound: false,
    canManagePartners: false,
    canManageAllTenants: false,
    canImpersonateAnyTenant: false,
    canAccessCrossTenantAudit: false,
    canManageMemberships: true, // firms with regional sub-tenants (Phase 4)
  },
  audit_firm: {
    hasPractitioners: true,
    hasPlaybookIp: false,
    ownsEvidenceVault: false,
    hasAuditWorkspace: true,
    canFederateOutbound: true,
    canFederateInbound: false,
    canManagePartners: false,
    canManageAllTenants: false,
    canImpersonateAnyTenant: false,
    canAccessCrossTenantAudit: false,
    canManageMemberships: true, // firms with regional sub-tenants (Phase 4)
  },
  regulator: {
    hasPractitioners: false,
    hasPlaybookIp: false,
    ownsEvidenceVault: false,
    hasAuditWorkspace: false,
    canFederateOutbound: false,
    canFederateInbound: true,
    canManagePartners: false,
    canManageAllTenants: false,
    canImpersonateAnyTenant: false,
    canAccessCrossTenantAudit: false,
    canManageMemberships: false,
  },
  platform_operator: {
    hasPractitioners: false,
    hasPlaybookIp: false,
    ownsEvidenceVault: false,
    hasAuditWorkspace: false,
    canFederateOutbound: false,
    canFederateInbound: false,
    canManagePartners: true, // legacy placeholder, still gates client portal sidebar for now
    canManageAllTenants: true, // NEW capability, only platform_operator has it
    canImpersonateAnyTenant: true, // NEW capability, only platform_operator has it
    canAccessCrossTenantAudit: true, // NEW capability, only platform_operator has it
    canManageMemberships: true,
  },
};

export function capabilitiesFor(value: string | null | undefined): ArchetypeCapabilities {
  return ROOT_ENTITY_CAPABILITIES[resolveRootEntityType(value)];
}
